import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
// import CampaignDetail from "../../Components/donasi/detaildonasi";
import NavbarThree from "../../Components/Navbar/navbarThree";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL!;

declare global {
  interface Window {
    fbq: any;
  }
}

interface Donation {
  id: number;
  name: string;
  amount: string;
  message: string;
  created_at: string;
}

interface Campaign {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  description: string;
  target_donation: string;
  total_donations: string;
  image: string;
  category?: {
    name: string;
  };
  user?: {
    name: string;
    image: string;
  };
}

const formatRupiah = (num: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(num);

const DetailDonasiPage = () => {
  const { slug } = useParams();
  const viewContentSent = useRef(false);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [activeTab, setActiveTab] = useState<"cerita" | "donatur">("cerita");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/campaign/${slug}`);

        setCampaign(res.data);
        setDonations(res.data.donations || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [slug]);

  // ✅ META PIXEL - VIEW CONTENT
  useEffect(() => {
    if (campaign && window.fbq && !viewContentSent.current) {
      window.fbq("track", "ViewContent", {
        content_name: campaign.title,
        content_ids: [campaign.id],
        content_type: "donation",
        value: Number(campaign.target_donation),
        currency: "IDR",
      });

      viewContentSent.current = true;
    }
  }, [campaign]);

  if (loading || !campaign) return <div className="p-6"></div>;

  const percent = Math.min((Number(campaign.total_donations) / Number(campaign.target_donation)) * 100, 100);

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} detik yang lalu`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} menit yang lalu`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} jam yang lalu`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} hari yang lalu`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} bulan yang lalu`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} tahun yang lalu`;
  };
  return (
    <div className="bg-gray-200 min-h-screen font-signika">
      {/* CONTAINER TENGAH */}
      <div className="max-w-2xl mx-auto bg-white min-h-screen relative">
        <NavbarThree />

        {/* spacer karena navbar fixed */}
        <div className="">
          <div className="pb-28 bg-gray-50 min-h-screen max-w-2xl mx-auto pt-28 max-sm:pt-16">
            {/* HERO */}

            <div className="relative w-full">
              <img src={`${campaign.image}`} className="w-full h-full max-sm:h-[320px] object-cover " alt={campaign.title} />

              {/* overlay gelap */}
              <div className="absolute inset-0 bg-black/60" />

              {/* CONTENT HERO */}
              <div className="absolute inset-0 flex flex-col justify-between px-6 pb-16 pt-6 text-white">
                {/* TOP SECTION */}
                <div className="flex justify-end items-start">
                  {/* Badge kategori kanan */}
                  <div className="bg-white/30 backdrop-blur px-4 py-1 rounded-full text-xs font-medium">{campaign.category?.name}</div>
                </div>

                {/* BOTTOM TEXT */}
                <div className="-mt-8">
                  <h1 className="text-2xl font-extrabold leading-snug">{campaign.title}</h1>

                  {/* USER INFO */}
                  <div className="flex items-center gap-3 mt-2">
                    <img src={`${API_BASE_URL}/uploads/users/${campaign.user?.image}`} className="w-10 h-10 rounded-full object-cover border-2 border-white" alt={campaign.user?.name} />
                    <div>
                      <div className="text-xs text-gray-300">INISIATOR</div>
                      <div className="text-sm font-semibold">{campaign.user?.name}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PROGRESS CARD */}
            <div className="relative -mt-10 px-4">
              <div className="bg-white rounded-3xl shadow-2xl p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-2xl font-bold text-primary2">{formatRupiah(Number(campaign.total_donations))}</div>
                    <div className="text-sm text-gray-500">
                      terkumpul dari <span className="font-medium">{formatRupiah(Number(campaign.target_donation))}</span>
                    </div>
                  </div>

                  <div className="text-gray-400 text-sm">∞</div>
                </div>

                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-4">
                  <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${percent}%` }} />
                </div>

                {/* Share Button */}
                <button className="mt-5 w-full border rounded-xl py-3 max-sm:py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">Bagikan ke Teman</button>
              </div>
            </div>

            {/* TABS */}
            <div className=" mx-auto mt-6 px-4">
              <div className="flex border-b">
                <button onClick={() => setActiveTab("cerita")} className={`flex-1 py-3 text-sm font-medium ${activeTab === "cerita" ? "border-b-2 border-primary2 text-primary2" : "text-gray-500"}`}>
                  Cerita
                </button>

                <button onClick={() => setActiveTab("donatur")} className={`flex-1 py-3 text-sm font-medium ${activeTab === "donatur" ? "border-b-2 border-primary2 text-primary2" : "text-gray-500"}`}>
                  Donatur ({donations.length})
                </button>
              </div>

              {/* TAB CONTENT */}
              <div className="mt-5 prose prose-sm max-w-none text-gray-700">
                {activeTab === "cerita" && <div className="mt-5 text-sm leading-7 text-gray-700 space-y-4" dangerouslySetInnerHTML={{ __html: campaign.description }} />}

                {activeTab === "donatur" && (
                  <div className="space-y-4">
                    {donations.map((d) => (
                      <div key={d.id} className="bg-white p-4 rounded-xl shadow-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold">{d.name}</div>
                            <div className="text-primary2 font-medium">{formatRupiah(Number(d.amount))}</div>
                          </div>

                          <div className="text-xs text-gray-400">{formatRelativeTime(d.created_at)}</div>
                        </div>

                        {d.message && <div className="text-gray-500 text-sm mt-2">{d.message}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* FIXED BUTTON */}
            {/* FIXED BUTTON */}
            <div className="fixed bottom-0 left-0 w-full px-4 py-4">
              <div className="max-w-xl mx-auto">
                <button
                  onClick={() => {
                    // ✅ META PIXEL - ADD TO CART
                    if (window.fbq && campaign) {
                      window.fbq("track", "AddToCart", {
                        content_name: campaign.title,
                        content_ids: [campaign.id],
                        content_type: "product",
                        value: Number(campaign.target_donation),
                        currency: "IDR",
                      });
                    }

                    // lanjut navigate
                    navigate(`/donate/${campaign.slug}/${campaign.id}`);
                  }}
                  className="
        w-full
        py-3
        rounded-full
        font-semibold
        text-lg
        text-white
        bg-gradient-to-r
        from-[#1b6f8f]
        to-primary2
        shadow-[0_0_20px_#2596be]
        hover:scale-[1.02]
        transition-all
        duration-300
      ">
                  PATUNGAN SEKARANG ❤
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailDonasiPage;
